/**
 * Pure helpers behind the URL state of a filterable listing (TRA-1397):
 * the Trajectories page and the Requests view read their range, sort,
 * page and metadata filters from the address bar through these, so a
 * shared link opens the same table on either surface.
 *
 * Every parser degrades a mistyped or unknown value to the default. A
 * shared link with `range=nope` must open on the default window, never
 * 422 the request and blank the table.
 *
 * Kept free of React so the decisions run in the node vitest setup.
 */

import { langsmithTraceIdOf } from '@/components/trajectories/langsmithImport'
import { parseTraceKey } from '@/components/traces/traceKey'

export interface SortState<F extends string> {
  field: F
  desc: boolean
}

/** A dropdown entry: `value` is what the URL and the server filter on,
    `label` is what the person reads. They differ only for tenants, where
    the filter is the id header and the name is shown when the client sent
    one. */
export interface FilterOption {
  value: string
  label: string
}

/** The metadata dropdowns both listings offer. The URL param name is the
    server's query param name on every listing endpoint. */
export type MetadataParam = 'model' | 'tenant' | 'environment' | 'traffic' | 'session' | 'initiator'

/** How a listing turns one row into the dropdown values it contributes. */
export interface MetadataFilterSpec<Item, P extends MetadataParam = MetadataParam> {
  param: P
  label: string
  values: (item: Item) => FilterOption[]
}

/** One metadata dropdown as the toolbar sees it: the URL selection plus
    the distinct values on the current page. */
export interface MetadataFilter<P extends MetadataParam = MetadataParam> {
  param: P
  label: string
  selected: string
  options: FilterOption[]
}

/** The dropdown value for "no filter". Never written to the URL. */
export const ANY = 'any'

export const plainFilterValue = (value: string | null | undefined): FilterOption[] =>
  value == null ? [] : [{ value, label: value }]

export function parseRange<R extends string>(
  param: string | null,
  ranges: readonly R[],
  fallback: R,
): R {
  return ranges.includes(param as R) ? (param as R) : fallback
}

/** `field.desc` / `field.asc` as the Trajectories page writes it. A bare
    field reads as ascending; an unknown field reads as the default. */
export function parseSort<F extends string>(
  param: string | null,
  fields: readonly F[],
  fallback: SortState<F>,
): SortState<F> {
  if (param === null) return fallback
  const desc = param.endsWith('.desc')
  const field = param.replace(/\.(asc|desc)$/, '')
  if (!fields.includes(field as F)) return fallback
  return { field: field as F, desc }
}

/** The URL value for a sort, or null when it is the default and the param
    should be dropped. */
export function sortParamValue<F extends string>(
  sort: SortState<F>,
  fallback: SortState<F>,
): string | null {
  if (sort.field === fallback.field && sort.desc === fallback.desc) return null
  return `${sort.field}.${sort.desc ? 'desc' : 'asc'}`
}

/** A second click on the active column flips its direction. A first click
    sorts ascending, except on the default column, which takes its default
    direction (newest first for a time column). */
export function nextSort<F extends string>(
  current: SortState<F>,
  field: F,
  fallback: SortState<F>,
): SortState<F> {
  if (current.field === field) return { field, desc: !current.desc }
  return { field, desc: field === fallback.field ? fallback.desc : false }
}

/**
 * A search that names one request or run exactly: a pasted UUID (a trace,
 * conversation or LangSmith run id), a LangSmith link, or a full trace key,
 * the three shapes the api turns into the gateway's indexed `runId` seek
 * (TRA-1344). The same criteria as the api's `run_id_of`, so the web holds
 * the range for exactly the searches the server treats as an id.
 */
export function isIdSearch(search: string): boolean {
  const text = search.trim()
  return langsmithTraceIdOf(text) !== null || parseTraceKey(text) !== null
}

/**
 * The time range the listing should use for this search. An id names one
 * request of any age: an imported run keeps its real date, and a support
 * ticket can be about last month. So an id search covers all time, and the
 * picked range stands for every other search (TRA-1169).
 */
export function rangeForSearch<R extends string>(search: string, range: R): R | 'all' {
  return isIdSearch(search) ? 'all' : range
}

/**
 * What the search box shows after the URL's `q` changed. Typing writes the
 * trimmed box text to the URL, so when the two agree the change was the
 * box's own and its text (trailing space and all) stays. When they differ,
 * navigation changed `q` behind the box (the back button, a link to this
 * page), and the box takes the URL's text so the address bar and the
 * results agree.
 */
export function searchAfterUrlChange(urlSearch: string, search: string): string {
  return urlSearch === search.trim() ? search : urlSearch
}

/** 1-based. Anything that is not a whole number from 1 up is page 1. */
export function parsePage(param: string | null): number {
  const page = Number(param ?? '1')
  return Number.isInteger(page) && page >= 1 ? page : 1
}

/** The next params after one change. Any filter, search, range or sort
    change restarts at page 1: the old page number points into a
    different list. */
export function changedParams(
  prev: URLSearchParams,
  mutate: (params: URLSearchParams) => void,
): URLSearchParams {
  const params = new URLSearchParams(prev)
  params.delete('page')
  mutate(params)
  return params
}

/** The dropdowns for a page of rows: each filter's URL selection plus the
    distinct values on the page, sorted by label. */
export function metadataFilters<Item, P extends MetadataParam>(
  specs: readonly MetadataFilterSpec<Item, P>[],
  searchParams: URLSearchParams,
  items: readonly Item[],
): MetadataFilter<P>[] {
  return specs.map((spec) => ({
    param: spec.param,
    label: spec.label,
    selected: searchParams.get(spec.param) ?? ANY,
    options: [...new Map(items.flatMap(spec.values).map((o) => [o.value, o])).values()].sort(
      (a, b) => a.label.localeCompare(b.label),
    ),
  }))
}

/** The server query params for the metadata filters the URL selects. */
export function metadataQuery<P extends MetadataParam>(
  searchParams: URLSearchParams,
  params: readonly P[],
): Partial<Record<P, string>> {
  const query: Partial<Record<P, string>> = {}
  for (const param of params) {
    const value = searchParams.get(param)
    if (value !== null && value !== ANY) query[param] = value
  }
  return query
}

/** How many filters are active, for the Filters button's count. */
export function activeFilterCount<P extends MetadataParam>(
  filters: readonly MetadataFilter<P>[],
  selectedTags: readonly string[] = [],
): number {
  return filters.filter((f) => f.selected !== ANY).length + selectedTags.length
}
